import {
  HStack,
  Heading,
  Text,
  HeadingProps,
  IconButton,
} from "@wootingkb/component-library";
import { FaCopy } from "react-icons/fa";
import { ReactNode, useCallback } from "react";
import { useLingui } from "@lingui/react";
import { useClipboard } from "../../utils/hooks/useClipboard";
import { useSuccessToast } from "../../utils/showToast";
import {
  COPIED_TO_CLIPBOARD_TEXT,
  COPY_TO_CLIPBOARD_ARIA_LABEL,
} from "../../utils/constants/i18n-strings";

interface Props {
  title: ReactNode;
  subtitle?: string;
  copy?: string;
  children?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

export default function PageTitle({
  title,
  subtitle,
  copy,
  children,
  size,
  ...rest
}: Props & Omit<HeadingProps, "title">) {
  const { _: i18n } = useLingui();
  const { onCopy } = useClipboard(copy ?? "");
  const showSuccessToast = useSuccessToast();

  const copyToClipboard = useCallback(() => {
    onCopy();
    showSuccessToast(i18n(COPIED_TO_CLIPBOARD_TEXT));
  }, [onCopy, showSuccessToast, i18n]);

  return (
    <HStack className="mb-6 items-center justify-between">
      <HStack className="items-center">
        <Heading size={size ?? "h1"} weight="bold" {...rest}>
          {title}
        </Heading>
        {copy && (
          <IconButton
            className="hidden md:grid"
            size="sm"
            onClick={copyToClipboard}
            aria-label={i18n(COPY_TO_CLIPBOARD_ARIA_LABEL)}
          >
            <FaCopy />
          </IconButton>
        )}
      </HStack>
      {subtitle && (
        <Text className="text-sm italic opacity-80">{subtitle}</Text>
      )}

      {children}
    </HStack>
  );
}
